import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPublicMenus, getPublicHeader, getPublicSettings } from '../services/api';

const DynamicNavbar = ({ onContactClick, onTrainingClick }) => {
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [header, setHeader] = useState({});
  const [settings, setSettings] = useState({});
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isTopMenuOpen, setIsTopMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState(false);
  const [detectedBgColor, setDetectedBgColor] = useState(null);
  const logoRef = useRef(null);

  // Default fallback data when server is not available
  const defaultMenus = [
    { name: 'Home', path: '#home', order: 1, icon: '🏠', isActive: true },
    { name: 'About Us', path: '#about', order: 2, icon: 'ℹ️', isActive: true },
    { name: 'Products', path: '#products', order: 3, icon: '📦', isActive: true },
    { name: 'Services', path: '#services', order: 4, icon: '⚙️', isActive: true },
    { name: 'Training', path: '#training', order: 5, icon: '🎓', isActive: true },
    { name: 'Partners', path: '#partners', order: 6, icon: '🤝', isActive: true },
    { name: 'Team', path: '#team', order: 7, icon: '👥', isActive: true },
    { name: 'Testimonials', path: '#testimonials', order: 8, icon: '💬', isActive: true },
    { name: 'Events', path: '#events', order: 9, icon: '📅', isActive: true },
    { name: 'Why Us', path: '#whyus', order: 10, icon: '⭐', isActive: true },
    { name: 'Career', path: '#career', order: 11, icon: '💼', isActive: true }
  ];

  const defaultHeader = {
    companyName: 'INTELIHEADS INTERNET Pvt. Ltd',
    tagline: 'AI & Robotics in Education',
    headerBackgroundColor: '#0A192F',
    headerTextColor: '#FFFFFF',
    logoUrl: ''
  };

  const defaultSettings = {
    siteTitle: 'INTELIHEADS INTERNET Pvt. Ltd',
    tagline: 'AI & Robotics in Education',
    contactEmail: 'global@inteliheads.com',
    phone: '+91-8076842489',
    address: 'Noida | Munich | Singapore'
  };

  // Function to detect dominant color from logo image
  const detectLogoBackgroundColor = (imageUrl) => {
    return new Promise((resolve) => {
      if (!imageUrl) {
        resolve('#0A192F');
        return;
      }

      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = imageUrl;

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          const sampleSize = 100;
          canvas.width = sampleSize;
          canvas.height = sampleSize;
          ctx.drawImage(img, 0, 0, sampleSize, sampleSize);

          const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize);
          const data = imageData.data;
          
          const edgePixels = [];
          const edgeWidth = 5;
          
          for (let y = 0; y < sampleSize; y++) {
            for (let x = 0; x < sampleSize; x++) {
              if (x < edgeWidth || x >= sampleSize - edgeWidth || 
                  y < edgeWidth || y >= sampleSize - edgeWidth) {
                const idx = (y * sampleSize + x) * 4;
                edgePixels.push({
                  r: data[idx],
                  g: data[idx + 1],
                  b: data[idx + 2]
                });
              }
            }
          }

          const colorCounts = {};
          edgePixels.forEach(pixel => {
            const key = `${Math.round(pixel.r/10)*10},${Math.round(pixel.g/10)*10},${Math.round(pixel.b/10)*10}`;
            colorCounts[key] = (colorCounts[key] || 0) + 1;
          });

          let maxCount = 0;
          let dominantColor = { r: 10, g: 25, b: 47 };
          
          Object.entries(colorCounts).forEach(([key, count]) => {
            if (count > maxCount) {
              maxCount = count;
              const [r, g, b] = key.split(',').map(Number);
              dominantColor = { r, g, b };
            }
          });

          const hexColor = `#${dominantColor.r.toString(16).padStart(2, '0')}${dominantColor.g.toString(16).padStart(2, '0')}${dominantColor.b.toString(16).padStart(2, '0')}`;
          resolve(hexColor);
        } catch (error) {
          console.error('Error detecting logo background color:', error);
          resolve('#0A192F');
        }
      };

      img.onerror = () => {
        console.error('Failed to load image for color detection');
        resolve('#0A192F');
      };
    });
  };

  useEffect(() => {
    loadCachedData();
    fetchDynamicData();
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Detect logo color after logo loads
  useEffect(() => {
    const logoUrl = getLogoUrl();
    if (logoUrl && logoUrl !== '' && logoUrl !== '/logo.png') {
      detectLogoBackgroundColor(logoUrl).then(color => {
        setDetectedBgColor(color);
        setHeader(prev => ({
          ...prev,
          headerBackgroundColor: color,
          headerTextColor: isDarkColor(color) ? '#FFFFFF' : '#0A192F'
        }));
      });
    }
  }, [header.logoUrl, settings.logoUrl]);

  const loadCachedData = () => {
    const cachedMenus = localStorage.getItem('cached_menus');
    const cachedHeader = localStorage.getItem('cached_header');
    const cachedSettings = localStorage.getItem('cached_settings');
    
    if (cachedMenus) {
      try {
        const parsedMenus = JSON.parse(cachedMenus);
        setMenus(parsedMenus);
      } catch (e) {
        console.error('Error parsing cached menus:', e);
      }
    }
    
    if (cachedHeader) {
      try {
        const parsedHeader = JSON.parse(cachedHeader);
        if (parsedHeader.headerBackgroundColor) {
          setHeader(parsedHeader);
          setDetectedBgColor(parsedHeader.headerBackgroundColor);
        }
      } catch (e) {
        console.error('Error parsing cached header:', e);
      }
    }
    
    if (cachedSettings) {
      try {
        setSettings(JSON.parse(cachedSettings));
      } catch (e) {
        console.error('Error parsing cached settings:', e);
      }
    }
  };

  const saveToCache = (menusData, headerData, settingsData) => {
    if (menusData && menusData.length > 0) {
      localStorage.setItem('cached_menus', JSON.stringify(menusData));
    }
    if (headerData && Object.keys(headerData).length > 0) {
      localStorage.setItem('cached_header', JSON.stringify(headerData));
    }
    if (settingsData && Object.keys(settingsData).length > 0) {
      localStorage.setItem('cached_settings', JSON.stringify(settingsData));
    }
  };

  const fetchDynamicData = async () => {
    try {
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 5000)
      );
      
      const fetchPromise = Promise.all([
        getPublicMenus(),
        getPublicHeader(),
        getPublicSettings()
      ]);
      
      const [menusResponse, headerResponse, settingsResponse] = await Promise.race([fetchPromise, timeoutPromise]);
      
      let menusArray = [];
      if (menusResponse) {
        if (menusResponse.data && Array.isArray(menusResponse.data)) {
          menusArray = menusResponse.data;
        } else if (Array.isArray(menusResponse)) {
          menusArray = menusResponse;
        } else if (menusResponse.success && menusResponse.data) {
          menusArray = menusResponse.data;
        }
      }
      
      if (menusArray.length > 0) {
        setMenus(menusArray);
      }
      
      let headerData = {};
      if (headerResponse) {
        if (headerResponse.data && typeof headerResponse.data === 'object') {
          headerData = headerResponse.data;
        } else {
          headerData = headerResponse;
        }
      }
      
      let settingsData = {};
      if (settingsResponse) {
        if (settingsResponse.data && typeof settingsResponse.data === 'object') {
          settingsData = settingsResponse.data;
        } else {
          settingsData = settingsResponse;
        }
      }
      
      const mergedData = { ...headerData, ...settingsData };
      
      const logoUrl = mergedData.logoUrl || '';
      if (logoUrl && logoUrl !== '' && logoUrl !== '/logo.png') {
        const detectedColor = await detectLogoBackgroundColor(logoUrl);
        setDetectedBgColor(detectedColor);
        mergedData.headerBackgroundColor = detectedColor;
        mergedData.headerTextColor = isDarkColor(detectedColor) ? '#FFFFFF' : '#0A192F';
      }
      
      setHeader(mergedData);
      setSettings(settingsData);
      
      saveToCache(menusArray, mergedData, settingsData);
      setServerError(false);
      
    } catch (error) {
      console.error('Error fetching dynamic data:', error);
      setServerError(true);
      
      if (menus.length === 0) {
        setMenus(defaultMenus);
      }
      if (Object.keys(header).length === 0) {
        setHeader(defaultHeader);
      }
      if (Object.keys(settings).length === 0) {
        setSettings(defaultSettings);
      }
    } finally {
      setLoading(false);
    }
  };

  const isDarkColor = (hexColor) => {
    if (!hexColor) return true;
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
  };

  const scrollToSection = (sectionId) => {
    const elementId = sectionId.replace('#', '');
    const element = document.getElementById(elementId);
    
    if (element) {
      const navbarHeight = document.querySelector('.desktop-nav')?.offsetHeight || 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    } else {
      if (elementId === 'home' || elementId === '') {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
    
    setIsMobileMenuOpen(false);
    setIsTopMenuOpen(false);
  };

  const getLogoUrl = () => {
    return header.logoUrl || settings.logoUrl || '';
  };

  const handleContactClick = () => {
    navigate('/contact');
  };

  if (loading && menus.length === 0) {
    return (
      <div style={{ 
        background: '#0A192F', 
        padding: '1rem', 
        textAlign: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        color: 'white'
      }}>
        <i className="fas fa-spinner fa-spin" style={{ color: '#2C7A7B' }}></i> Loading...
      </div>
    );
  }

  const displayMenus = menus.length > 0 ? menus : defaultMenus;
  const displayHeader = Object.keys(header).length > 0 ? header : defaultHeader;
  const displaySettings = Object.keys(settings).length > 0 ? settings : defaultSettings;

  const companyName = displayHeader.companyName || displaySettings.companyName || displaySettings.siteTitle || 'INTELIHEADS INTERNET Pvt. Ltd';
  const tagline = displayHeader.tagline || displaySettings.tagline || 'AI & Robotics in Education';
  
  const headerBgColor = detectedBgColor || displayHeader.headerBackgroundColor || '#0A192F';
  const headerTextColor = isDarkColor(headerBgColor) ? '#FFFFFF' : '#0A192F';
  
  const logoUrl = getLogoUrl();

  const isDarkBg = isDarkColor(headerBgColor);

  return (
    <>
      {/* Top Header */}
      <div style={{
        background: headerBgColor,
        borderBottom: `1px solid ${isDarkBg ? 'rgba(255,255,255,0.1)' : '#eaecef'}`,
        padding: '0.8rem 0',
        position: 'relative',
        zIndex: 1001,
        transition: 'background-color 0.3s ease'
      }}>
        <div className="container" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          {/* Logo Area */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '1.2rem',
            cursor: 'pointer',
            flex: '0 0 auto',
            maxWidth: '45%',
            minWidth: '300px'
          }} 
          onClick={() => scrollToSection('#home')}>
            {logoUrl && logoUrl !== '' && logoUrl !== '/logo.png' ? (
              <>
                <img 
                  ref={logoRef}
                  src={logoUrl} 
                  alt="Logo" 
                  style={{ 
                    height: '60px',
                    width: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                    maxWidth: '250px',
                    flexShrink: 0
                  }}
                  onError={(e) => {
                    console.error('Logo failed to load:', logoUrl);
                    e.target.style.display = 'none';
                    const parent = e.target.parentElement;
                    const fallback = document.createElement('span');
                    fallback.style.cssText = `font-size: 1.4rem; font-weight: 700; color: ${headerTextColor};`;
                    fallback.textContent = 'INTELIHEADS';
                    parent.insertBefore(fallback, e.target.nextSibling);
                  }}
                  onLoad={() => {
                    detectLogoBackgroundColor(logoUrl).then(color => {
                      if (color && color !== headerBgColor) {
                        setDetectedBgColor(color);
                      }
                    });
                  }}
                />
                <div style={{ 
                  flex: 1,
                  minWidth: '150px'
                }}>
                  <div style={{ 
                    fontSize: 'clamp(1rem, 3.5vw, 1.3rem)',
                    fontWeight: 700,
                    color: headerTextColor,
                    lineHeight: '1.3',
                    letterSpacing: '0.5px'
                  }}>
                    {companyName}
                  </div>
                  <div style={{ 
                    fontSize: 'clamp(0.65rem, 1.5vw, 0.8rem)',
                    color: isDarkBg ? 'rgba(255,255,255,0.8)' : '#5A6E7F', 
                    marginTop: '3px',
                    fontWeight: 500,
                    letterSpacing: '0.3px'
                  }}>
                    {tagline}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ 
                fontSize: 'clamp(1.2rem, 4vw, 1.6rem)',
                fontWeight: 700, 
                color: headerTextColor,
                lineHeight: '1.3',
                padding: '0 10px'
              }}>
                {companyName}
              </div>
            )}
          </div>

          {/* Server Status Indicator */}
          {serverError && (
            <div style={{ 
              position: 'absolute', 
              bottom: '-30px', 
              left: '50%', 
              transform: 'translateX(-50%)',
              background: '#F5A623',
              color: 'white',
              padding: '2px 10px',
              borderRadius: '4px',
              fontSize: '0.7rem',
              whiteSpace: 'nowrap'
            }}>
              <i className="fas fa-server"></i> Offline Mode - Using cached data
            </div>
          )}

          {/* Desktop Top Right Menu */}
          <div className="desktop-top-menu" style={{ 
            display: 'flex', 
            gap: '1.5rem', 
            alignItems: 'center',
            color: headerTextColor,
            flexShrink: 0
          }}>
            <a href="#" style={{ 
              textDecoration: 'none', 
              color: isDarkBg ? 'rgba(255,255,255,0.8)' : '#4a627a', 
              fontSize: '0.85rem', 
              fontWeight: 500,
              transition: 'color 0.2s',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => e.target.style.color = isDarkBg ? 'white' : '#2C7A7B'}
            onMouseLeave={(e) => e.target.style.color = isDarkBg ? 'rgba(255,255,255,0.8)' : '#4a627a'}
            >
              Webshop
            </a>
            <a href="#" style={{ 
              textDecoration: 'none', 
              color: isDarkBg ? 'rgba(255,255,255,0.8)' : '#4a627a', 
              fontSize: '0.85rem', 
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => e.target.style.color = isDarkBg ? 'white' : '#2C7A7B'}
            onMouseLeave={(e) => e.target.style.color = isDarkBg ? 'rgba(255,255,255,0.8)' : '#4a627a'}
            >
              Downloads
            </a>
            <a href="#" style={{ 
              textDecoration: 'none', 
              color: isDarkBg ? 'rgba(255,255,255,0.8)' : '#4a627a', 
              fontSize: '0.85rem', 
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => e.target.style.color = isDarkBg ? 'white' : '#2C7A7B'}
            onMouseLeave={(e) => e.target.style.color = isDarkBg ? 'rgba(255,255,255,0.8)' : '#4a627a'}
            >
              FAQ
            </a>
            <button 
              onClick={handleContactClick}
              style={{
                background: isDarkBg ? 'rgba(255,255,255,0.15)' : 'transparent',
                border: `1.5px solid ${isDarkBg ? 'rgba(255,255,255,0.5)' : '#2C7A7B'}`,
                padding: '0.5rem 1.5rem',
                borderRadius: '30px',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: isDarkBg ? 'white' : '#2C7A7B',
                cursor: 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = isDarkBg ? 'white' : '#2C7A7B';
                e.target.style.color = isDarkBg ? '#0A192F' : 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = isDarkBg ? 'rgba(255,255,255,0.15)' : 'transparent';
                e.target.style.color = isDarkBg ? 'white' : '#2C7A7B';
              }}
            >
              Contact
            </button>
            <button 
              onClick={() => navigate('/admin/login')}
              style={{
                background: isDarkBg ? 'rgba(255,255,255,0.2)' : 'transparent',
                border: `1.5px solid ${isDarkBg ? 'rgba(255,255,255,0.5)' : '#2C7A7B'}`,
                padding: '0.5rem 1.5rem',
                borderRadius: '30px',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: isDarkBg ? 'white' : '#2C7A7B',
                cursor: 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = isDarkBg ? 'white' : '#2C7A7B';
                e.target.style.color = isDarkBg ? '#0A192F' : 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = isDarkBg ? 'rgba(255,255,255,0.2)' : 'transparent';
                e.target.style.color = isDarkBg ? 'white' : '#2C7A7B';
              }}
            >
              Login
            </button>
          </div>

          {/* Mobile Menu Buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button 
              onClick={() => setIsTopMenuOpen(!isTopMenuOpen)} 
              className="mobile-top-menu-btn" 
              style={{
                background: isDarkBg ? 'rgba(255,255,255,0.2)' : '#2C7A7B',
                border: 'none',
                fontSize: '0.9rem',
                cursor: 'pointer',
                padding: '0.5rem 1rem',
                display: 'none',
                color: 'white',
                borderRadius: '8px'
              }}
            >
              <i className="fas fa-user-circle"></i> Menu
            </button>
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
              className="mobile-menu-btn" 
              style={{
                background: isDarkBg ? 'rgba(255,255,255,0.2)' : '#2C7A7B',
                border: 'none',
                fontSize: '1.2rem',
                cursor: 'pointer',
                padding: '0.5rem 0.8rem',
                display: 'none',
                color: 'white',
                borderRadius: '8px'
              }}
            >
              {isMobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Main Navigation */}
      <div className="desktop-nav" style={{
        background: isDarkBg ? 'rgba(10,25,47,0.95)' : '#FFFFFF',
        borderBottom: `1px solid ${isDarkBg ? 'rgba(255,255,255,0.1)' : '#eaecef'}`,
        position: 'sticky',
        top: 0,
        zIndex: 999,
        boxShadow: isScrolled ? `0 2px 10px ${isDarkBg ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)'}` : 'none',
        backdropFilter: isDarkBg ? 'blur(10px)' : 'none'
      }}>
        <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2rem' }}>
          <ul style={{ 
            display: 'flex', 
            gap: '0', 
            listStyle: 'none', 
            margin: 0, 
            padding: 0, 
            justifyContent: 'flex-start', 
            flexWrap: 'wrap' 
          }}>
            {displayMenus.filter(menu => menu.isActive !== false).map((menu, index) => (
              <li key={menu._id || index}>
                <button 
                  onClick={() => {
                    const targetId = menu.path === '/' ? '#home' : menu.path;
                    scrollToSection(targetId);
                  }} 
                  style={{
                    padding: '0 1rem',
                    height: '55px',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    color: isDarkBg ? 'rgba(255,255,255,0.85)' : '#1f2d3d',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'color 0.2s'
                  }} 
                  onMouseEnter={(e) => e.target.style.color = isDarkBg ? 'white' : '#2C7A7B'} 
                  onMouseLeave={(e) => e.target.style.color = isDarkBg ? 'rgba(255,255,255,0.85)' : '#1f2d3d'}
                >
                  {menu.icon && <span style={{ marginRight: '0.5rem' }}>{menu.icon}</span>}
                  {menu.name}
                  <span style={{
                    position: 'absolute',
                    bottom: '-1px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '0',
                    height: '3px',
                    background: isDarkBg ? 'white' : '#2C7A7B',
                    transition: 'width 0.25s ease'
                  }} className="nav-hover-line" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {isTopMenuOpen && (
        <>
          <div onClick={() => setIsTopMenuOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000 }} />
          <div style={{ position: 'absolute', top: '75px', right: '1rem', background: 'white', borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)', zIndex: 1001, minWidth: '200px' }}>
            <a href="#" style={{ display: 'block', padding: '0.8rem 1.2rem', textDecoration: 'none', color: '#4a627a', borderBottom: '1px solid #f0f0f0' }}><i className="fas fa-shopping-cart"></i> Webshop</a>
            <a href="#" style={{ display: 'block', padding: '0.8rem 1.2rem', textDecoration: 'none', color: '#4a627a', borderBottom: '1px solid #f0f0f0' }}><i className="fas fa-download"></i> Downloads</a>
            <a href="#" style={{ display: 'block', padding: '0.8rem 1.2rem', textDecoration: 'none', color: '#4a627a', borderBottom: '1px solid #f0f0f0' }}><i className="fas fa-question-circle"></i> FAQ</a>
            <button onClick={handleContactClick} style={{ width: '100%', padding: '0.8rem 1.2rem', border: 'none', background: 'white', textAlign: 'left', cursor: 'pointer' }}><i className="fas fa-envelope"></i> Contact</button>
            <button onClick={() => navigate('/admin/login')} style={{ width: '100%', padding: '0.8rem 1.2rem', border: 'none', background: 'white', textAlign: 'left', cursor: 'pointer', fontWeight: 600, color: '#2C7A7B' }}><i className="fas fa-sign-in-alt"></i> Login</button>
          </div>
        </>
      )}

      {/* Mobile Side Menu */}
      {isMobileMenuOpen && (
        <>
          <div onClick={() => setIsMobileMenuOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000 }} />
          <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: 'min(320px, 85%)', background: '#0A192F', boxShadow: '4px 0 20px rgba(0,0,0,0.15)', zIndex: 2001, overflowY: 'auto', animation: 'slideInFromLeft 0.3s ease' }}>
            <div style={{ background: '#0A192F', padding: '1.8rem 1.5rem', color: 'white' }}>
              {logoUrl && logoUrl !== '' && logoUrl !== '/logo.png' && (
                <img 
                  src={logoUrl} 
                  alt="Logo" 
                  style={{ height: '45px', width: 'auto', marginBottom: '0.5rem' }}
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>{companyName}</div>
              <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>{tagline}</div>
              <button onClick={() => setIsMobileMenuOpen(false)} style={{ position: 'absolute', top: '1.2rem', right: '1.2rem', background: 'rgba(255,255,255,0.1)', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'white', padding: '0.4rem 0.7rem', borderRadius: '8px' }}>✕</button>
            </div>
            <div style={{ padding: '1rem 0', background: '#0A192F' }}>
              {displayMenus.filter(menu => menu.isActive !== false).map((menu, index) => (
                <button 
                  key={menu._id || index} 
                  onClick={() => {
                    const targetId = menu.path === '/' ? '#home' : menu.path;
                    scrollToSection(targetId);
                  }} 
                  style={{ 
                    width: '100%', 
                    textAlign: 'left', 
                    padding: '0.9rem 1.5rem', 
                    background: 'none', 
                    border: 'none', 
                    fontSize: '1rem', 
                    cursor: 'pointer', 
                    color: 'rgba(255,255,255,0.85)', 
                    fontWeight: 500,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.05)';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'none';
                    e.target.style.color = 'rgba(255,255,255,0.85)';
                  }}
                >
                  {menu.icon && <span style={{ marginRight: '1rem' }}>{menu.icon}</span>}
                  {menu.name}
                </button>
              ))}
            </div>
            <div style={{ 
              padding: '1.5rem', 
              borderTop: '1px solid rgba(255,255,255,0.1)',
              background: '#0A192F'
            }}>
              <button 
                onClick={handleContactClick}
                style={{ 
                  width: '100%', 
                  marginBottom: '0.75rem', 
                  background: '#2C7A7B', 
                  color: 'white', 
                  padding: '0.8rem', 
                  borderRadius: '12px', 
                  border: 'none', 
                  fontWeight: 600, 
                  cursor: 'pointer' 
                }}
              >
                Contact Sales
              </button>
              <button 
                onClick={onTrainingClick} 
                style={{ 
                  width: '100%', 
                  border: '2px solid rgba(255,255,255,0.5)',
                  color: 'white',
                  background: 'transparent', 
                  padding: '0.8rem', 
                  borderRadius: '12px', 
                  fontWeight: 600, 
                  cursor: 'pointer' 
                }}
              >
                Training Programs
              </button>
            </div>
          </div>
        </>
      )}

      <style>{`
        @media (max-width: 992px) {
          .mobile-menu-btn, .mobile-top-menu-btn { display: block !important; }
          .desktop-nav, .desktop-top-menu { display: none !important; }
        }
        @keyframes slideInFromLeft {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .nav-hover-line {
          display: none;
        }
        .desktop-nav button:hover .nav-hover-line {
          display: block;
          width: 70%;
        }
        @media (max-width: 768px) {
          .container .logo-area {
            max-width: 60% !important;
            min-width: 200px !important;
          }
        }
        @media (max-width: 480px) {
          .container .logo-area {
            max-width: 70% !important;
            min-width: 150px !important;
          }
        }
      `}</style>
    </>
  );
};

export default DynamicNavbar;